import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Icon } from 'components/Icons'
import { SERVICES, ROLE_LABELS, getCompletionColor } from 'pages/Services/servicesCatalog'
import { ROLE_INSTRUCTIONS, COMMON_PRINCIPLES } from './roleInstructions'

import styles from './HelpPage.module.scss'

// Группы ролей для секций.
const ROLE_GROUPS = [
  {
    key: 'academic',
    title: 'Учебный процесс',
    icon: 'graduation',
    color: '#3b82f6',
    roles: ['student', 'teacher', 'curator', 'head_of_department', 'dean', 'methodist'],
  },
  {
    key: 'hr',
    title: 'Кадры',
    icon: 'users',
    color: '#10b981',
    roles: ['hr_students', 'hr_employees'],
  },
  {
    key: 'finance',
    title: 'Финансы и техническая работа',
    icon: 'wallet',
    color: '#ef4444',
    roles: ['accountant', 'archivist', 'dorm_staff', 'proctor'],
  },
  {
    key: 'management',
    title: 'Управление',
    icon: 'cog',
    color: '#f59e0b',
    roles: ['rectorate', 'sysadmin'],
  },
  {
    key: 'students',
    title: 'Специальные категории студентов',
    icon: 'student',
    color: '#8b5cf6',
    roles: ['foreign_student', 'postgrad', 'dpo_student'],
  },
  {
    key: 'external',
    title: 'Внешние пользователи',
    icon: 'user',
    color: '#14b8a6',
    roles: ['parent', 'applicant'],
  },
]

const ROLE_DESCRIPTIONS = {
  student: 'Базовая учётка студента: расписание, журнал, тесты, заявления, ВКР, оплата, общежитие, библиотека.',
  teacher: 'Преподаватель: журнал и оценки, конструктор тестов, банк вопросов, ведомости, плагиат, наука, нагрузка.',
  curator: 'Преподаватель + детализация по своей группе, оправдательные документы, студенческая жизнь.',
  head_of_department: 'Заведующий кафедрой: нагрузка, журналы кафедры, согласование учебных планов, сотрудники.',
  dean: 'Декан факультета: дашборд факультета, утверждение ОПОП, приказы, отчёты по аккредитации.',
  methodist: 'Методист — самая полная роль: расписание, ОПОП-конструктор, сессии, государственные отчёты.',
  hr_students: 'Отдел кадров «Студенты»: реестр, приёмная кампания, переводы, отчисления, иностранные студенты.',
  hr_employees: 'Отдел кадров «Сотрудники»: реестр, мастер найма, кадровые приказы, оргструктура.',
  accountant: 'Бухгалтерия: кассовые ордера, контракты, зарплаты, фонды. 2FA обязательна.',
  archivist: 'Архивист: 3 архива (университет, студенты, сотрудники), OCR-поиск, связь приказов с делами. 2FA обязательна.',
  dorm_staff: 'Сотрудник общежития: корпуса, комнаты, заселение, инциденты, оплата.',
  proctor: 'Проктор: контроль академической честности на онлайн-экзаменах.',
  rectorate: 'Ректорат: полный доступ к админ-панели, аккредитация, интеграции. 2FA обязательна.',
  sysadmin: 'Системный администратор: API-документация, аудит-журнал, управление ролями и пользователями. 2FA обязательна.',
  parent: 'Родитель: успеваемость и расписание ребёнка, библиотека.',
  applicant: 'Абитуриент: подача документов на поступление, выбор направлений (модуль в разработке).',
  foreign_student: 'Студент-иностранец: всё что у студента + отдельная карточка с визами и регистрацией.',
  postgrad: 'Магистрант / аспирант: всё что у студента + диссертация, научный руководитель, публикации.',
  dpo_student: 'Слушатель ДПО: курсы повышения квалификации, сертификаты.',
}

function HelpPage() {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  // Множественное раскрытие: клик переключает только эту карточку,
  // остальные остаются в текущем состоянии.
  const [activeRoles, setActiveRoles] = useState(() => new Set())

  const toggleRole = (code) => {
    setActiveRoles((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }
  const expandAll = () => {
    setActiveRoles(new Set(Object.keys(ROLE_LABELS)))
  }
  const collapseAll = () => {
    setActiveRoles(new Set())
  }

  const roleServices = useMemo(() => {
    const map = {}
    Object.keys(ROLE_LABELS).forEach((r) => {
      map[r] = SERVICES.filter((s) => s.roles.includes(r))
    })
    return map
  }, [])

  const q = query.trim().toLowerCase()
  const matches = (text) => !q || (text || '').toLowerCase().includes(q)

  const filteredGroups = useMemo(() => {
    if (!q) return ROLE_GROUPS
    return ROLE_GROUPS.map((g) => {
      const filteredRoles = g.roles.filter((r) => {
        const label = ROLE_LABELS[r] || r
        const desc = ROLE_DESCRIPTIONS[r] || ''
        const services = roleServices[r] || []
        const inst = ROLE_INSTRUCTIONS[r] || {}
        const purposeMatch = (inst.purpose || []).some((p) => matches(p))
        const scenariosMatch = (inst.scenarios || []).some(
          (sc) => matches(sc.title) || (sc.steps || []).some((st) => matches(typeof st === 'string' ? st : st.step)),
        )
        const faqMatch = (inst.faq || []).some((f) => matches(f.q) || matches(f.a))
        const screensMatch = (inst.screens || []).some(
          (s) => matches(s.name) || matches(s.purpose),
        )
        return (
          matches(label) ||
          matches(desc) ||
          purposeMatch ||
          scenariosMatch ||
          faqMatch ||
          screensMatch ||
          services.some((s) => matches(s.title) || matches(s.description))
        )
      })
      return { ...g, roles: filteredRoles }
    }).filter((g) => g.roles.length > 0)
  }, [q, roleServices])

  const handleOpenService = (path) => {
    if (path) navigate(path)
  }

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div className={styles.hero__icon}>
          <Icon name="help-circle" size={32} color="var(--color-accent)" />
        </div>
        <div className={styles.hero__text}>
          <h1>{t('helpPage.title', 'Справка по ролям и сервисам')}</h1>
          <p>
            {t(
              'helpPage.subtitle',
              'Полное руководство пользователя: что доступно каждой роли, как работают ключевые сервисы, где найти инструкции и как связаться с поддержкой.',
            )}
          </p>
        </div>
        <div className={styles.hero__actions}>
          <div className={styles.hero__search}>
            <Icon name="search" size={18} color="var(--color-text-muted)" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('helpPage.search_placeholder', 'Поиск по ролям, сервисам, действиям…')}
              aria-label={t('helpPage.search_placeholder', 'Поиск')}
            />
          </div>
          <div className={styles.hero__buttons}>
            <button
              type="button"
              className={styles.heroBtn}
              onClick={expandAll}
              title={t('helpPage.expand_all', 'Развернуть все')}
            >
              <Icon name="chevron-down" size={14} color="currentColor" />
              {t('helpPage.expand_all', 'Развернуть все')}
            </button>
            <button
              type="button"
              className={styles.heroBtn}
              onClick={collapseAll}
              disabled={activeRoles.size === 0}
              title={t('helpPage.collapse_all', 'Свернуть все')}
            >
              <Icon name="chevron-up" size={14} color="currentColor" />
              {t('helpPage.collapse_all', 'Свернуть все')}
            </button>
          </div>
        </div>
      </div>

      {/* Общие принципы — для всех ролей */}
      <section className={styles.principles}>
        <header className={styles.principles__head}>
          <Icon name="badge-check" size={22} color="var(--color-accent)" />
          <h2>{t('helpPage.common_title', 'Общие принципы для всех ролей')}</h2>
        </header>
        <div className={styles.principles__grid}>
          <div className={styles.principles__card}>
            <h3>
              <Icon name="key" size={16} color="currentColor" />
              {t('helpPage.common_login', 'Вход и безопасность')}
            </h3>
            <ul>
              {COMMON_PRINCIPLES.login.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </div>
          <div className={styles.principles__card}>
            <h3>
              <Icon name="grid" size={16} color="currentColor" />
              {t('helpPage.common_layout', 'Структура личного кабинета')}
            </h3>
            <ul>
              {COMMON_PRINCIPLES.layout.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </div>
          <div className={styles.principles__card}>
            <h3>
              <Icon name="globe" size={16} color="currentColor" />
              {t('helpPage.common_languages', 'Языки')}
            </h3>
            <p>{COMMON_PRINCIPLES.languages}</p>
          </div>
        </div>
      </section>

      {filteredGroups.length === 0 ? (
        <div className={styles.empty}>{t('helpPage.no_results', 'Ничего не найдено')}</div>
      ) : (
        filteredGroups.map((group) => (
          <section key={group.key} className={styles.group}>
            <header className={styles.group__head} style={{ borderColor: group.color }}>
              <span
                className={styles.group__icon}
                style={{ background: `${group.color}1f`, color: group.color }}
              >
                <Icon name={group.icon} size={22} color="currentColor" />
              </span>
              <h2>{t(`helpPage.group_${group.key}`, group.title)}</h2>
              <span className={styles.group__count}>
                {group.roles.length} {t('helpPage.roles_label', 'ролей')}
              </span>
            </header>

            <div className={styles.roles}>
              {group.roles.map((roleCode) => {
                const services = roleServices[roleCode] || []
                const isActive = activeRoles.has(roleCode)
                const inst = ROLE_INSTRUCTIONS[roleCode] || {}
                return (
                  <article
                    key={roleCode}
                    className={`${styles.role} ${isActive ? styles.role_active : ''}`}
                  >
                    <button
                      type="button"
                      className={styles.role__head}
                      onClick={() => toggleRole(roleCode)}
                      aria-expanded={isActive}
                    >
                      <div className={styles.role__title}>
                        <strong>{ROLE_LABELS[roleCode]}</strong>
                        <code className={styles.role__code}>{roleCode}</code>
                      </div>
                      <span className={styles.role__count}>
                        {services.length} {t('helpPage.services_label', 'сервисов')}
                      </span>
                      <Icon name={isActive ? 'chevron-up' : 'chevron-down'} size={18} />
                    </button>

                    <p className={styles.role__desc}>{ROLE_DESCRIPTIONS[roleCode]}</p>

                    {isActive && (
                      <div className={styles.role__body}>
                        {/* Цель / Назначение */}
                        {inst.purpose && inst.purpose.length > 0 && (
                          <div className={styles.section}>
                            <h4 className={styles.section__title}>
                              <Icon name="badge-check" size={14} color="var(--color-accent)" />
                              {t('helpPage.s_purpose', 'Назначение роли')}
                            </h4>
                            <ul className={styles.bullets}>
                              {inst.purpose.map((p, i) => (
                                <li key={i}>{p}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Вход */}
                        {inst.login && inst.login.length > 0 && (
                          <div className={styles.section}>
                            <h4 className={styles.section__title}>
                              <Icon name="key" size={14} color="var(--color-accent)" />
                              {t('helpPage.s_login', 'Вход в систему')}
                            </h4>
                            <ol className={styles.bullets}>
                              {inst.login.map((p, i) => (
                                <li key={i}>{p}</li>
                              ))}
                            </ol>
                          </div>
                        )}

                        {/* Главные экраны */}
                        {inst.screens && inst.screens.length > 0 && (
                          <div className={styles.section}>
                            <h4 className={styles.section__title}>
                              <Icon name="grid" size={14} color="var(--color-accent)" />
                              {t('helpPage.s_screens', 'Главные экраны')}
                            </h4>
                            <table className={styles.screensTable}>
                              <tbody>
                                {inst.screens.map((sc, i) => (
                                  <tr key={i}>
                                    <td className={styles.screensTable__name}>{sc.name}</td>
                                    <td className={styles.screensTable__purpose}>{sc.purpose}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Сценарии */}
                        {inst.scenarios && inst.scenarios.length > 0 && (
                          <div className={styles.section}>
                            <h4 className={styles.section__title}>
                              <Icon name="check-square" size={14} color="var(--color-accent)" />
                              {t('helpPage.s_scenarios', 'Типичные сценарии')}
                            </h4>
                            <div className={styles.scenarios}>
                              {inst.scenarios.map((sc, i) => (
                                <details key={i} className={styles.scenario}>
                                  <summary>{sc.title}</summary>
                                  <ol className={styles.bullets}>
                                    {(sc.steps || []).map((st, j) => (
                                      <li key={j}>{typeof st === 'string' ? st : st.step}</li>
                                    ))}
                                  </ol>
                                </details>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* FAQ */}
                        {inst.faq && inst.faq.length > 0 && (
                          <div className={styles.section}>
                            <h4 className={styles.section__title}>
                              <Icon name="help-circle" size={14} color="var(--color-accent)" />
                              {t('helpPage.s_faq', 'Частые вопросы')}
                            </h4>
                            <div className={styles.faq}>
                              {inst.faq.map((f, i) => (
                                <details key={i} className={styles.faq__item}>
                                  <summary>{f.q}</summary>
                                  <p>{f.a}</p>
                                </details>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Контакты роли (если есть) */}
                        {inst.contacts && inst.contacts.length > 0 && (
                          <div className={styles.section}>
                            <h4 className={styles.section__title}>
                              <Icon name="message-circle" size={14} color="var(--color-accent)" />
                              {t('helpPage.s_contacts', 'Контакты для этой роли')}
                            </h4>
                            <ul className={styles.bullets}>
                              {inst.contacts.map((c, i) => (
                                <li key={i}>{c}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Доступные сервисы */}
                        <div className={styles.section}>
                          <h4 className={styles.section__title}>
                            <Icon name="package" size={14} color="var(--color-accent)" />
                            {t('helpPage.available_services', 'Доступные сервисы')}
                            <span className={styles.section__counter}>{services.length}</span>
                          </h4>
                          <ul className={styles.serviceList}>
                            {services.map((svc) => (
                              <li key={svc.id}>
                                <button
                                  type="button"
                                  className={styles.serviceItem}
                                  onClick={() => handleOpenService(svc.path)}
                                >
                                  <span
                                    className={styles.serviceItem__pct}
                                    style={{ color: getCompletionColor(svc.completion) }}
                                    title={`${svc.completion}% готово`}
                                  >
                                    {svc.completion}%
                                  </span>
                                  <span className={styles.serviceItem__title}>
                                    {svc.title}
                                  </span>
                                  <span className={styles.serviceItem__desc}>
                                    {svc.description}
                                  </span>
                                  <Icon
                                    name="external"
                                    size={14}
                                    color="var(--color-text-muted)"
                                  />
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </article>
                )
              })}
            </div>
          </section>
        ))
      )}

      <section className={styles.contacts}>
        <h2>{t('helpPage.contacts_title', 'Контакты поддержки')}</h2>
        <div className={styles.contactsGrid}>
          <div className={styles.contactCard}>
            <Icon name="message-circle" size={20} color="var(--color-accent)" />
            <div>
              <strong>{t('helpPage.contact_chat', 'Чат поддержки')}</strong>
              <span>{t('helpPage.contact_chat_v', 'В системе → правый нижний угол')}</span>
              <small>{t('helpPage.contact_chat_t', 'Отвечаем в течение 1 рабочего дня')}</small>
            </div>
          </div>
          <div className={styles.contactCard}>
            <Icon name="user" size={20} color="var(--color-accent)" />
            <div>
              <strong>{t('helpPage.contact_email', 'Электронная почта')}</strong>
              <span>support@chu.kg</span>
              <small>{t('helpPage.contact_email_t', 'Для общих вопросов')}</small>
            </div>
          </div>
          <div className={styles.contactCard}>
            <Icon name="shield-check" size={20} color="var(--color-accent)" />
            <div>
              <strong>{t('helpPage.contact_security', 'Безопасность')}</strong>
              <span>support+security@chu.kg</span>
              <small>{t('helpPage.contact_security_t', 'Утечки, взломы — реакция за 30 мин')}</small>
            </div>
          </div>
          <div className={styles.contactCard}>
            <Icon name="alert-circle" size={20} color="#ef4444" />
            <div>
              <strong>{t('helpPage.contact_incident', 'Срочный инцидент')}</strong>
              <span>{t('helpPage.contact_incident_v', 'Зелёная кнопка в окне поддержки')}</span>
              <small>{t('helpPage.contact_incident_t', 'Критические сбои — 30 мин в рабочее время')}</small>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default HelpPage
